/**
 * @file shipment-sync-report.ts
 * @description Честный итог кнопки «Обновить статус» (phase19.13 шаг 6).
 *
 * Раньше тост говорил «Статус обновлён» на любой успешный запрос — менеджер
 * не понимал, приехали новые события или СДЭК просто промолчал.
 *
 * Итоги ленты теперь считает сервер (`addedEvents`, `currentStage`,
 * `lastEventAt`), поэтому здесь остался один запрос — сам синк. Раньше лента
 * читалась зондами до и после опроса: три запроса вместо одного и риск
 * разъехаться с сервером на параллельном вебхуке.
 */

import { ApiError } from "./api-client";
import { DELIVERY_STAGE_LABEL } from "./api-delivery-monitoring";
import {
  syncSdekShipmentStatus,
  type SyncShipmentStatusResult,
} from "./api-carriers-sdek";
import { pluralRu } from "./plural";
import { formatShopDateTime } from "./shop-time";

/** Что показать в тосте. */
export interface ShipmentSyncOutcome {
  severity: "success" | "info" | "error";
  message: string;
}

/**
 * Ответ сервера → фраза для менеджера. Отдельная чистая функция: правила
 * формулировок проверяются без сети.
 */
export function describeSyncResult(result: SyncShipmentStatusResult): ShipmentSyncOutcome {
  if (result.rejected) {
    const reasons = result.rejectionErrors?.length
      ? result.rejectionErrors.join("; ")
      : "причина не указана";
    return {
      severity: "error",
      message:
        `Заявка отклонена СДЭК: ${reasons}. ` +
        "Отгрузка возвращена в черновик — исправьте данные и передайте заново.",
    };
  }

  if (result.mock) {
    return {
      severity: "info",
      message: "СДЭК не опрашивался: заявка ещё не создана или интеграция не настроена.",
    };
  }

  if (result.addedEvents > 0) {
    const events = pluralRu(result.addedEvents, ["событие", "события", "событий"]);
    const stage = result.currentStage
      ? `, текущий — ${DELIVERY_STAGE_LABEL[result.currentStage]}`
      : "";
    return {
      severity: "success",
      message: `+${result.addedEvents} ${events}${stage}`,
    };
  }

  // Событий не прибавилось, но заявка могла обновиться иначе — например,
  // перевозчик наконец присвоил трек-номер.
  const lastSeen = result.lastEventAt
    ? ` Последнее событие — ${formatShopDateTime(result.lastEventAt)}`
    : "";
  if (result.updated) {
    return {
      severity: "success",
      message: `Данные отгрузки обновлены, новых событий нет.${lastSeen}`,
    };
  }

  return {
    severity: "info",
    message: result.lastEventAt
      ? `Новых событий нет.${lastSeen}`
      : "Событий от перевозчика ещё нет. Статус прежний.",
  };
}

/**
 * Опросить перевозчика и пересказать результат человеческим языком.
 *
 * Возможные исходы:
 *  - «+N событий, текущий — {этап}» — приехали новые события;
 *  - «Новых событий нет. Последнее — {дата}» — СДЭК ответил, но молчит;
 *  - «СДЭК не ответил…» — сеть или 5xx, статус остался прежним;
 *  - отказ по заявке — красный тост с причинами (отгрузка вернулась в черновик).
 */
export async function syncShipmentAndReport(
  shipmentId: string,
): Promise<ShipmentSyncOutcome> {
  try {
    return describeSyncResult(await syncSdekShipmentStatus(shipmentId));
  } catch (err) {
    // Недоступность перевозчика и наши 4xx — разные новости: первую менеджер
    // просто пережидает, во второй есть что чинить.
    const unreachable = err instanceof ApiError && (err.status === 0 || err.status >= 500);
    return {
      severity: "error",
      message:
        unreachable || !(err instanceof ApiError)
          ? "СДЭК не ответил. Статус прежний, попробуйте через минуту."
          : err.message,
    };
  }
}
